import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core';
import adaptivePlugin from '@fullcalendar/adaptive';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import RenovationIcon from '../images/home-renovation-icon.svg'
import DemolitionIcon from '../images/home-demolition-icon.svg'


export default class extends Controller {
  static targets = [ "root", "form" ]

  connect() {
    this.calendar = buildCalendar(this.rootTarget)
    loadDataAndRender(this.formTarget, this.calendar)
  }

  prev(e) {
    e.preventDefault()
    this.calendar.prev()
  }

  next(e) {
    e.preventDefault()
    this.calendar.next()
  }

  today(e) {
    e.preventDefault()
    this.calendar.today()
  }
}


// Le planning travaille au MOIS sur plusieurs annees, et les dates cote Rails
// (phases.start_on / end_on) sont des dates pleines et INCLUSIVES.
// FullCalendar, lui, traite `end` comme EXCLUSIF sur un evenement journee
// entiere : passer end_on tel quel fait s'arreter la barre a l'entree du
// dernier mois, d'ou l'impression de mois vide en fin de phase.
// Ces deux helpers sont le seul endroit ou la conversion a lieu.

// 'YYYY-MM-DD' -> jour suivant, pour la borne exclusive de FullCalendar.
const toExclusiveEnd = (dateString) => {
  if (!dateString) return null
  const d = new Date(`${dateString}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

// Date FullCalendar (UTC, cf. timeZone: 'UTC') -> 'YYYY-MM-DD', sans heure.
const toDateString = (date) => (date ? date.toISOString().slice(0, 10) : null)

// Borne exclusive de FullCalendar -> date de fin inclusive pour Rails.
const toInclusiveEnd = (date) => {
  if (!date) return null
  const d = new Date(date.getTime())
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

// Un evenement de phase, construit au meme endroit pour le chargement initial
// et pour le reaffichage apres enregistrement.
const phaseEvent = (phase) => ({
  id: phase.id,
  resourceId: phase.project_id,
  title: phase.planning_text,
  allDay: true,
  start: phase.start_on,
  end: toExclusiveEnd(phase.end_on),
  backgroundColor: PHASE_COLORS(phase.kind),
  constraint: {
    resourceIds: [ phase.project_id ]
  }
})

const buildCalendar = (root) => {
  return new Calendar(root, {
    plugins: [ adaptivePlugin, interactionPlugin, resourceTimelinePlugin ],
    schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",  
    locale: 'fr',
    timeZone: 'UTC',
    editable: true, // enable draggable Phases,
    selectable: false,
    // Pas de temps du planning : le metier raisonne au mois sur plusieurs
    // annees. Sans ces deux reglages, FullCalendar deduit le pas tout seul et
    // l'accroche du glisser-deposer peut tomber en plein mois.
    slotDuration: { months: 1 },
    snapDuration: { months: 1 },
    aspectRatio: 1.85,
    scrollTime: '00:00', // undo default 6am scrollTime
    headerToolbar: '',
    initialView: 'resourceTimelineYear',
    resourceOrder: 'title',
    views: {
      resourceTimelineYear: {
        type: 'resourceTimeline',
        duration: { years: 4 },
        nowIndicator: true
      }
    },
    viewDidMount: (arg) => {
      showYearSeparator(arg.el)
    },
    expandRows: false,
    stickyFooterScrollbar: true,
    windowResize: function(arg) {
      console.log('The calendar has adjusted to a window resize. Current view: ' + arg.view.type);
    },
    // Resources
    resourceAreaWidth: '20%',
    resourceAreaHeaderContent: 'Adresses',
    resources: [],
    resourceLabelDidMount: renderResourceLabel,
    // Events
    events: [],
    eventDrop: updateEvent,
    eventResize: updateEvent
  });
}


const loadDataAndRender = (form, calendar) => {
  const fetchOptions = {
    method: 'GET',
    mode: 'cors',
    cache: 'default'
  }
  fetch('/projects/planning_data?' + new URLSearchParams(serializeObject(form)), fetchOptions)
    .then(response => 
      response.json()
    )
    .then(data => {
      data.forEach(project => {
        calendar.addResource({
          id: project.id,
          title: project.full_address,
          housing_blocked: project.housing_blocked,
          kind: project.kind
        })
        project.planning_phases.forEach(phase => {
          calendar.addEvent(phaseEvent(phase))
        })
      })
    })
    .then(() => {
      calendar.render()
    })
}

const renderResourceLabel = (info) => {
  const linkEl = document.createElement('a')
  linkEl.setAttribute('href', `/projects/${info.resource.id}/d/housing`)
  const imgEl = document.createElement('img')
  imgEl.setAttribute('src', info.resource.extendedProps.kind === 'restoration' ? RenovationIcon : DemolitionIcon)
  Object.assign(imgEl.style, {
    height: '16px',
    'vertical-align': 'bottom'
  })
  Object.assign(linkEl.style, {
    height: '16px',
    display: 'inline-block',
    'margin-right': '6px',
    'vertical-align': 'sub'
  })
  linkEl.appendChild(imgEl)
  const mainEl = info.el.querySelector('.fc-datagrid-cell-main')
  mainEl.prepend(linkEl)
  mainEl.style.color = info.resource.extendedProps.housing_blocked ? 'red' : 'black'
}

const updateEvent = (e) => {
  // Sans le token CSRF, Rails rejette le PUT en 422 dans un before_action :
  // la requete n'atteint jamais PhasesController#update et RIEN n'est
  // enregistre. Le deplacement et le redimensionnement des phases sur le
  // planning n'ont donc jamais rien sauvegarde. Meme motif que
  // editable_cell_controller.js, qui lui l'envoie bien.
  const csrfToken = document.querySelector("[name='csrf-token']")?.content

  const myInit = {
    method: 'PUT',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    // Des dates pleines : envoyer e.event.start brut produisait un horodatage
    // ISO complet, et la fin exclusive de FullCalendar decalait la phase d'un
    // mois.
    body: JSON.stringify({ phase: {
      start_on: toDateString(e.event.start),
      end_on: toInclusiveEnd(e.event.end)
    }})
  }
  fetch('/phases/' + e.event.id, myInit)
    .then(response => {
      // Sans ce controle, un echec partait dans un .then() sans .catch() et
      // disparaissait en silence : l'utilisateur voyait sa barre bouger a
      // l'ecran alors que rien n'etait enregistre.
      if (!response.ok) {
        throw new Error(`PUT /phases/${e.event.id} : HTTP ${response.status}`)
      }
      return response.json()
    })
    .then(json => {
      // we want to rerender all resource events with server dates
      const calendar = e.view.calendar
      const resource = e.event.getResources()[0]
      const events = resource.getEvents()
      json.planning_phases.forEach(phase => {
        const event = events.find(event => event.id === phase.id.toString())
        if (event) {
          // { allDay: true } est indispensable : setDates applique
          // `{ allDay: options.allDay }` comme propriete standard
          // (@fullcalendar/core internal-common.js), donc sans l'option
          // l'evenement perd son caractere journee entiere et FullCalendar
          // prefixe son titre avec l'heure — le fameux « 0h ».
          event.setDates(phase.start_on, toExclusiveEnd(phase.end_on), { allDay: true })
        } else {
          calendar.addEvent(phaseEvent(phase))
        }
      })
    })
    .catch(error => {
      console.error('[planning]', error)
      // On remet la barre ou elle etait : l'ecran ne doit pas montrer un
      // planning que la base ne contient pas.
      e.revert()
      window.alert("Le deplacement n'a pas pu etre enregistre. La phase a ete remise a sa place.")
    })
}

const PHASE_COLORS = (kind) => {
  return {
    // Housing phases
    'diagnosis':         'blue',
    'building_permit':   'green',
    'works_rfp':         'orange',
    'preliminary_works': 'red',
    'works_start':       'purple',
    'works_acceptance':  'brown',
    'works_inspection':  'silver',
    // Demolition phases
    'demolition_permit':    '#059669', // emerald-600
    'demolition_rfp':       '#d97706', // amber-600
    'demolition_start':     '#dc2626', // red-600
    'demolition_acceptance': '#7c3aed' // violet-600
  }[kind]
}

const serializeObject = (form) => {
  // Create a new FormData object
  const formData = new FormData(form);

  // Create an object to hold the name/value pairs
  const pairs = {};

  // Add each name/value pair to the object
  for (const [name, value] of formData) {
    pairs[name] = value;
  }

  // Return the object
  return pairs;
}

const showYearSeparator = (el) => {
  const yearCells = el.querySelectorAll("td[data-date$='01-01']")
  yearCells.forEach(cell => {
    cell.style.borderColor = 'darkGray'
    cell.style.borderWidth = '2px'
  })
}
